import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  abandonChannelDispatchFailures,
  approveChannelFanout,
  ChannelApprovalDetail,
  ChannelApprovalFailure,
  ChannelApprovalLine,
  ChannelApprovalSummary,
  getChannelApprovalDetail,
  listChannelApprovals,
  rejectChannelFanout,
  removeChannelApprovalVariants,
  retryChannelDispatchFailures,
  updateChannelApprovalPrices,
} from '../api/channelApprovals';
import { colors, spacing, typography, typographyFlat, borderRadius } from '../styles/tokens';
import { formatInstant } from '../utils/datetime';

const formatMoney = (value: number | null | undefined) => {
  if (value === null || value === undefined || Number.isNaN(value)) {
    return '—';
  }
  return `$${value.toFixed(2)}`;
};

const formatTimestamp = formatInstant;

/** Display / draft string for a line's editable sell price. */
export function draftPriceForApprovalLine(line: ChannelApprovalLine): string {
  if (line.missing) return '';
  const effective = line.preferred_price_override ?? line.effective_base_price ?? line.calculated_preferred_price;
  return effective !== null && effective !== undefined ? String(effective) : '';
}

export function approvalPricesEqual(left: string, right: string): boolean {
  const a = left.trim();
  const b = right.trim();
  if (!a && !b) return true;
  const na = Number.parseFloat(a);
  const nb = Number.parseFloat(b);
  if (Number.isNaN(na) || Number.isNaN(nb)) {
    return a === b;
  }
  return Math.round(na * 100) === Math.round(nb * 100);
}

/**
 * Only lines whose draft differs from the loaded baseline become override writes.
 * Unchanged drafts must not stamp preferred_price_override (policy fan-out approve bug).
 */
export function buildDirtyApprovalPriceUpdates(
  lines: ChannelApprovalLine[],
  draftPrices: Record<string, string>,
  baselinePrices: Record<string, string>,
): Array<{ variant_id: string; preferred_price_override: number | null }> {
  const updates: Array<{ variant_id: string; preferred_price_override: number | null }> = [];
  for (const line of lines) {
    if (line.missing) continue;
    const raw = (draftPrices[line.variant_id] ?? '').trim();
    const baseline = (baselinePrices[line.variant_id] ?? '').trim();
    if (approvalPricesEqual(raw, baseline)) {
      continue;
    }
    if (!raw) {
      updates.push({ variant_id: line.variant_id, preferred_price_override: null });
      continue;
    }
    const parsed = Number.parseFloat(raw);
    if (Number.isNaN(parsed) || parsed < 0) {
      throw new Error(`Invalid price for ${line.card_name || line.variant_id}`);
    }
    updates.push({ variant_id: line.variant_id, preferred_price_override: parsed });
  }
  return updates;
}

export const ChannelApprovalsPage: React.FC = () => {
  const [items, setItems] = useState<ChannelApprovalSummary[]>([]);
  const [failures, setFailures] = useState<ChannelApprovalFailure[]>([]);
  const [total, setTotal] = useState(0);
  const [failureTotal, setFailureTotal] = useState(0);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [detail, setDetail] = useState<ChannelApprovalDetail | null>(null);
  const [draftPrices, setDraftPrices] = useState<Record<string, string>>({});
  const [baselinePrices, setBaselinePrices] = useState<Record<string, string>>({});
  const [isLoadingList, setIsLoadingList] = useState(false);
  const [isLoadingDetail, setIsLoadingDetail] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isApproving, setIsApproving] = useState(false);
  const [isRejecting, setIsRejecting] = useState(false);
  const [removingVariantId, setRemovingVariantId] = useState<string | null>(null);
  const [isAbandoning, setIsAbandoning] = useState(false);
  const [isRetryingCredentialFailures, setIsRetryingCredentialFailures] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const loadList = useCallback(async () => {
    setIsLoadingList(true);
    setError(null);
    try {
      const response = await listChannelApprovals();
      setItems(response.items);
      setTotal(response.total);
      setFailures(response.failures || []);
      setFailureTotal(response.failure_total || 0);
      if (response.items.length > 0 && !selectedId) {
        setSelectedId(response.items[0].outbox_event_id);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load approvals');
    } finally {
      setIsLoadingList(false);
    }
  }, [selectedId]);

  const applyDetail = useCallback((response: ChannelApprovalDetail) => {
    setDetail(response);
    const nextDraft: Record<string, string> = {};
    response.lines.forEach((line) => {
      nextDraft[line.variant_id] = draftPriceForApprovalLine(line);
    });
    setDraftPrices(nextDraft);
    setBaselinePrices(nextDraft);
  }, []);

  const loadDetail = useCallback(async (outboxEventId: string) => {
    setIsLoadingDetail(true);
    setError(null);
    try {
      const response = await getChannelApprovalDetail(outboxEventId);
      applyDetail(response);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load approval detail');
      setDetail(null);
    } finally {
      setIsLoadingDetail(false);
    }
  }, [applyDetail]);

  useEffect(() => {
    void loadList();
  }, [loadList]);

  useEffect(() => {
    if (selectedId) {
      void loadDetail(selectedId);
    }
  }, [selectedId, loadDetail]);

  const selectedSummary = useMemo(
    () => items.find((item) => item.outbox_event_id === selectedId) ?? null,
    [items, selectedId],
  );

  const busy = isSaving || isApproving || isRejecting || removingVariantId !== null;

  const handlePriceChange = (variantId: string, value: string) => {
    setDraftPrices((prev) => ({ ...prev, [variantId]: value }));
  };

  const buildPriceUpdates = (lines: ChannelApprovalLine[]) =>
    buildDirtyApprovalPriceUpdates(lines, draftPrices, baselinePrices);

  const handleSavePrices = async () => {
    if (!selectedId || !detail) return;
    setIsSaving(true);
    setError(null);
    setMessage(null);
    try {
      const updates = buildPriceUpdates(detail.lines);
      if (updates.length === 0) {
        setMessage('No price edits to save (unchanged drafts are not written as overrides).');
        return;
      }
      const refreshed = await updateChannelApprovalPrices(selectedId, updates);
      applyDetail(refreshed);
      setMessage(`Saved preferred-price overrides for ${refreshed.variants_updated ?? updates.length} variant(s).`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save prices');
    } finally {
      setIsSaving(false);
    }
  };

  const handleApprove = async () => {
    if (!selectedId || !detail) return;
    setIsApproving(true);
    setError(null);
    setMessage(null);
    try {
      // Only persist overrides for lines the operator actually edited.
      // Unchanged drafts used to mass-stamp preferred_price_override on every
      // policy fan-out approval (thousands of sticky overrides).
      const updates = buildPriceUpdates(detail.lines);
      if (updates.length > 0) {
        await updateChannelApprovalPrices(selectedId, updates);
      }
      const result = await approveChannelFanout(selectedId);
      const overrideNote = updates.length > 0
        ? ` Saved ${updates.length} override edit(s).`
        : '';
      setMessage(
        `Approved fan-out (${result.approved_queue_ids.length} queue entries).`
        + `${overrideNote} Dispatcher will sync shortly.`,
      );
      setSelectedId(null);
      setDetail(null);
      await loadList();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to approve fan-out');
    } finally {
      setIsApproving(false);
    }
  };

  const handleReject = async () => {
    if (!selectedId) return;
    const isPolicyBatch = detail?.aggregate_type === 'policy_pricing_batch';
    const confirmed = window.confirm(
      isPolicyBatch
        ? 'Cancel this policy pricing fan-out?\n\n'
          + 'Channel sync will not run. Deferred policy_price stamps are not written.\n'
          + 'Catalog sell base stays as it was before this apply.'
        : 'Delete this approval batch?\n\n'
          + 'Removes unapproved intake copies (pending_approval) from SyncBase and cancels channel fan-out.\n'
          + 'Already sellable stock (in_stock) is not deleted — use this for wrong pending batches only.',
    );
    if (!confirmed) return;

    setIsRejecting(true);
    setError(null);
    setMessage(null);
    try {
      const result = await rejectChannelFanout(selectedId);
      const deleted = (result as { copies_deleted?: number }).copies_deleted;
      setMessage(
        `Deleted approval (${result.rejected_queue_ids.length} queue entries cancelled`
        + (deleted !== undefined ? `; ${deleted} pending copie(s) removed` : '')
        + ').',
      );
      setSelectedId(null);
      setDetail(null);
      await loadList();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete approval');
    } finally {
      setIsRejecting(false);
    }
  };

  const handleRemoveLine = async (line: ChannelApprovalLine) => {
    if (!selectedId) return;
    const label = line.card_name || line.variant_id;
    const confirmed = window.confirm(
      `Remove "${label}" from this approval?\n\n`
      + 'Deletes this line\'s pending_approval copies and excludes it from channel sync.',
    );
    if (!confirmed) return;

    setRemovingVariantId(line.variant_id);
    setError(null);
    setMessage(null);
    try {
      const result = await removeChannelApprovalVariants(selectedId, [line.variant_id]);
      if (result.rejected) {
        setMessage('Removed last variant(s); approval deleted.');
        setSelectedId(null);
        setDetail(null);
        await loadList();
        return;
      }
      applyDetail(result);
      setMessage(`Removed ${result.removed_count ?? 1} variant(s) from approval.`);
      await loadList();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to remove variant');
    } finally {
      setRemovingVariantId(null);
    }
  };

  const handleAbandonPriceFailures = async () => {
    const priceFailures = failures.filter((f) => f.is_permanent_price_failure && f.status !== 'failed');
    if (priceFailures.length === 0 && failures.every((f) => f.status === 'failed')) {
      setMessage('No active price-failure retries to abandon.');
      return;
    }
    const confirmed = window.confirm(
      'Abandon all pending "No base price" dispatch retries?\n\n'
      + 'They will stop clogging the dispatcher. Fix prices later and re-sync via quantity backfill if needed.',
    );
    if (!confirmed) return;

    setIsAbandoning(true);
    setError(null);
    setMessage(null);
    try {
      const result = await abandonChannelDispatchFailures({ abandon_all_price_failures: true });
      setMessage(`Abandoned ${result.abandoned_count} price-failure job(s).`);
      await loadList();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to abandon jobs');
    } finally {
      setIsAbandoning(false);
    }
  };

  const credentialFailureCount = useMemo(
    () => failures.filter((failure) => failure.is_credential_failure).length,
    [failures],
  );

  const handleRetryCredentialFailures = async () => {
    if (credentialFailureCount === 0) {
      setMessage('No credential-failure dispatches to retry.');
      return;
    }
    const confirmed = window.confirm(
      `Re-queue ${credentialFailureCount} failed dispatch job(s) that failed due to missing credentials?\n\n`
      + 'Use this after unsealing Vault or restoring channel secrets.',
    );
    if (!confirmed) return;

    setIsRetryingCredentialFailures(true);
    setError(null);
    setMessage(null);
    try {
      const result = await retryChannelDispatchFailures({ retry_credential_failures_only: true });
      setMessage(`Re-queued ${result.retried_count} credential-failure job(s). Dispatcher will retry shortly.`);
      await loadList();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to re-queue jobs');
    } finally {
      setIsRetryingCredentialFailures(false);
    }
  };

  const previewLine = (line: ChannelApprovalLine) => {
    const raw = draftPrices[line.variant_id]?.trim();
    const base = raw ? Number.parseFloat(raw) : line.effective_base_price;
    if (base === null || base === undefined || Number.isNaN(base)) {
      return { shopify: null, manapool: null };
    }
    return {
      shopify: line.shopify_multiplier !== undefined ? base * line.shopify_multiplier : null,
      manapool: line.manapool_multiplier !== undefined ? base * line.manapool_multiplier : null,
    };
  };

  return (
    <div style={styles.page}>
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>Channel Approvals</h1>
          <p style={styles.subtitle}>
            Review batch fan-out prices before inventory becomes sellable and syncs to Shopify and Manapool.
            Multi-variant intake submits wait here until you Approve & Sync. Order sales and quantity restocks dispatch immediately.
          </p>
        </div>
        <button type="button" style={styles.secondaryButton} onClick={() => void loadList()} disabled={isLoadingList}>
          {isLoadingList ? 'Refreshing…' : 'Refresh'}
        </button>
      </div>

      {error && <div style={styles.errorBanner}>{error}</div>}
      {message && <div style={styles.successBanner}>{message}</div>}

      {failureTotal > 0 && (
        <section style={styles.failurePanel}>
          <div style={styles.failureHeader}>
            <div>
              <strong>Failed / stuck dispatches ({failureTotal})</strong>
              <div style={styles.listItemMeta}>
                Includes price failures and credential/Vault auth failures (e.g. after server restart).
              </div>
            </div>
            <div style={styles.actionRow}>
              {credentialFailureCount > 0 && (
                <button
                  type="button"
                  style={styles.primaryButton}
                  onClick={() => void handleRetryCredentialFailures()}
                  disabled={isRetryingCredentialFailures}
                >
                  {isRetryingCredentialFailures
                    ? 'Re-queueing…'
                    : `Re-queue credential failures (${credentialFailureCount})`}
                </button>
              )}
              <button
                type="button"
                style={styles.dangerButton}
                onClick={() => void handleAbandonPriceFailures()}
                disabled={isAbandoning}
              >
                {isAbandoning ? 'Abandoning…' : 'Abandon No-Price Retries'}
              </button>
            </div>
          </div>
          <div style={styles.tableWrap}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Queue</th>
                  <th style={styles.th}>Channel</th>
                  <th style={styles.th}>Status</th>
                  <th style={styles.th}>Retries</th>
                  <th style={styles.th}>Created</th>
                  <th style={styles.th}>Error</th>
                </tr>
              </thead>
              <tbody>
                {failures.slice(0, 25).map((failure) => (
                  <tr key={failure.queue_id}>
                    <td style={styles.td}>{failure.queue_id}</td>
                    <td style={styles.td}>{failure.target_channel || '—'}</td>
                    <td style={styles.td}>{failure.status}</td>
                    <td style={styles.td}>{failure.retry_count}</td>
                    <td style={styles.td}>{formatTimestamp(failure.created_at)}</td>
                    <td style={styles.td}>
                      <span style={styles.errorText}>
                        {failure.is_credential_failure ? '[credential] ' : ''}
                        {(failure.last_error || '—').slice(0, 140)}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      <div style={styles.layout}>
        <section style={styles.listPanel}>
          <div style={styles.panelHeader}>
            <strong>Pending ({total})</strong>
          </div>
          {items.length === 0 && !isLoadingList && (
            <div style={styles.emptyState}>No batches waiting for approval.</div>
          )}
          {items.map((item) => {
            const isActive = item.outbox_event_id === selectedId;
            return (
              <button
                key={item.outbox_event_id}
                type="button"
                style={{
                  ...styles.listItem,
                  ...(isActive ? styles.listItemActive : {}),
                }}
                onClick={() => setSelectedId(item.outbox_event_id)}
              >
                <div style={styles.listItemTitle}>{item.batch_name || 'Inventory batch'}</div>
                <div style={styles.listItemMeta}>
                  {item.variant_count} variant{item.variant_count === 1 ? '' : 's'} · {item.target_channels.join(', ') || 'channels'}
                </div>
                <div style={styles.listItemMeta}>Queued {formatTimestamp(item.queued_at || item.occurred_at)}</div>
              </button>
            );
          })}
        </section>

        <section style={styles.detailPanel}>
          {!selectedSummary || !detail ? (
            <div style={styles.emptyState}>
              {isLoadingDetail ? 'Loading approval detail…' : 'Select a pending batch to review prices.'}
            </div>
          ) : (
            <>
              <div style={styles.detailHeader}>
                <div>
                  <h2 style={styles.detailTitle}>{selectedSummary.batch_name || 'Inventory batch'}</h2>
                  <div style={styles.detailMeta}>
                    Submitted {formatTimestamp(selectedSummary.batch_submitted_at || selectedSummary.occurred_at)}
                    {' · '}
                    {detail.lines.length} lines
                    {detail.lines_missing_price > 0 ? ` · ${detail.lines_missing_price} missing price` : ''}
                  </div>
                  {selectedSummary.last_error && (
                    <div style={styles.warningText}>{selectedSummary.last_error}</div>
                  )}
                </div>
                <div style={styles.actionRow}>
                  <button
                    type="button"
                    style={styles.dangerButton}
                    onClick={() => void handleReject()}
                    disabled={busy}
                  >
                    {isRejecting ? 'Deleting…' : 'Delete Batch'}
                  </button>
                  <button
                    type="button"
                    style={styles.secondaryButton}
                    onClick={() => void handleSavePrices()}
                    disabled={busy}
                    title="Only saves lines whose price you edited; stamps preferred_price_override"
                  >
                    {isSaving ? 'Saving…' : 'Save Price Edits'}
                  </button>
                  <button
                    type="button"
                    style={styles.primaryButton}
                    onClick={() => void handleApprove()}
                    disabled={busy}
                    title="Approves fan-out; only edited prices become preferred_price_override"
                  >
                    {isApproving ? 'Approving…' : 'Approve & Sync'}
                  </button>
                </div>
              </div>

              <div style={styles.tableWrap}>
                <table style={styles.table}>
                  <thead>
                    <tr>
                      <th style={styles.th}>Card</th>
                      <th style={styles.th}>Variant</th>
                      <th style={styles.th}>Qty</th>
                      <th style={styles.th}>Calculated</th>
                      <th style={styles.th}>Proposed</th>
                      <th style={styles.th}>Listing Price</th>
                      <th style={styles.th}>Shopify</th>
                      <th style={styles.th}>Manapool</th>
                      <th style={styles.th} />
                    </tr>
                  </thead>
                  <tbody>
                    {detail.lines.filter((line) => !line.missing).map((line) => {
                      const preview = previewLine(line);
                      const isRemoving = removingVariantId === line.variant_id;
                      return (
                        <tr key={line.variant_id}>
                          <td style={styles.td}>
                            <div style={styles.cardName}>{line.card_name}</div>
                            <div style={styles.cardMeta}>{line.set_name || '—'}</div>
                          </td>
                          <td style={styles.td}>
                            {line.condition}
                            {line.is_foil ? ' · Foil' : ''}
                            {' · '}
                            {line.language}
                          </td>
                          <td style={styles.td}>{line.quantity ?? 0}</td>
                          <td style={styles.td}>{formatMoney(line.calculated_preferred_price)}</td>
                          <td style={styles.td}>
                            {line.proposed_policy_price != null
                              ? formatMoney(line.proposed_policy_price)
                              : '—'}
                            {line.current_effective_base_price != null
                              && line.proposed_policy_price != null
                              && line.current_effective_base_price !== line.effective_base_price
                              ? (
                                <div style={styles.cardMeta}>
                                  now {formatMoney(line.current_effective_base_price)}
                                </div>
                              )
                              : null}
                          </td>
                          <td style={styles.td}>
                            <input
                              type="number"
                              min="0"
                              step="0.01"
                              value={draftPrices[line.variant_id] ?? ''}
                              onChange={(event) => handlePriceChange(line.variant_id, event.target.value)}
                              style={styles.priceInput}
                              disabled={busy}
                            />
                          </td>
                          <td style={styles.td}>{formatMoney(preview.shopify)}</td>
                          <td style={styles.td}>{formatMoney(preview.manapool)}</td>
                          <td style={styles.td}>
                            <button
                              type="button"
                              style={styles.linkDangerButton}
                              onClick={() => void handleRemoveLine(line)}
                              disabled={busy}
                              title="Remove from this approval"
                            >
                              {isRemoving ? '…' : 'Remove'}
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </section>
      </div>
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  page: {
    padding: spacing.lg,
    display: 'flex',
    flexDirection: 'column',
    gap: spacing.md,
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: spacing.md,
  },
  title: {
    fontSize: typographyFlat.fontSize.xxl,
    fontWeight: typographyFlat.fontWeight.bold,
    margin: 0,
    color: colors.text,
  },
  subtitle: {
    ...typography.body,
    color: colors.textSecondary,
    margin: `${spacing.xs} 0 0`,
  },
  layout: {
    display: 'grid',
    gridTemplateColumns: '320px 1fr',
    gap: spacing.md,
    alignItems: 'start',
  },
  listPanel: {
    backgroundColor: colors.white,
    border: `1px solid ${colors.border}`,
    borderRadius: borderRadius.md,
    overflow: 'hidden',
  },
  panelHeader: {
    padding: spacing.sm,
    borderBottom: `1px solid ${colors.border}`,
    backgroundColor: colors.gray50,
  },
  listItem: {
    width: '100%',
    textAlign: 'left',
    padding: spacing.sm,
    border: 'none',
    borderBottom: `1px solid ${colors.border}`,
    backgroundColor: colors.white,
    cursor: 'pointer',
  },
  listItemActive: {
    backgroundColor: colors.gray100,
  },
  listItemTitle: {
    fontWeight: 600,
    color: colors.text,
    marginBottom: spacing.xs,
  },
  listItemMeta: {
    fontSize: typography.small.fontSize,
    color: colors.textSecondary,
  },
  detailPanel: {
    backgroundColor: colors.white,
    border: `1px solid ${colors.border}`,
    borderRadius: borderRadius.md,
    padding: spacing.md,
    minHeight: '480px',
  },
  detailHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: spacing.md,
    marginBottom: spacing.md,
  },
  detailTitle: {
    fontSize: typographyFlat.fontSize.xl,
    fontWeight: typographyFlat.fontWeight.semibold,
    margin: 0,
  },
  detailMeta: {
    fontSize: typography.small.fontSize,
    color: colors.textSecondary,
    marginTop: spacing.xs,
  },
  warningText: {
    marginTop: spacing.xs,
    fontSize: typography.small.fontSize,
    color: colors.warning,
  },
  actionRow: {
    display: 'flex',
    gap: spacing.sm,
    alignItems: 'flex-start',
    flexWrap: 'wrap' as const,
  },
  tableWrap: {
    overflowX: 'auto',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
  },
  th: {
    textAlign: 'left',
    fontSize: typography.small.fontSize,
    color: colors.textSecondary,
    padding: `${spacing.xs} ${spacing.sm}`,
    borderBottom: `1px solid ${colors.border}`,
  },
  td: {
    padding: `${spacing.sm}`,
    borderBottom: `1px solid ${colors.border}`,
    verticalAlign: 'top',
    fontSize: typography.small.fontSize,
  },
  cardName: {
    fontWeight: 600,
    color: colors.text,
  },
  cardMeta: {
    color: colors.textSecondary,
    marginTop: '2px',
  },
  priceInput: {
    width: '96px',
    padding: `${spacing.xs} ${spacing.sm}`,
    border: `1px solid ${colors.border}`,
    borderRadius: borderRadius.sm,
  },
  primaryButton: {
    padding: `${spacing.xs} ${spacing.md}`,
    borderRadius: borderRadius.sm,
    border: 'none',
    backgroundColor: colors.primary,
    color: colors.white,
    cursor: 'pointer',
  },
  secondaryButton: {
    padding: `${spacing.xs} ${spacing.md}`,
    borderRadius: borderRadius.sm,
    border: `1px solid ${colors.border}`,
    backgroundColor: colors.white,
    color: colors.text,
    cursor: 'pointer',
  },
  emptyState: {
    padding: spacing.lg,
    color: colors.textSecondary,
    textAlign: 'center',
  },
  errorBanner: {
    padding: spacing.sm,
    borderRadius: borderRadius.sm,
    backgroundColor: colors.errorLight,
    color: colors.error,
  },
  successBanner: {
    padding: spacing.sm,
    borderRadius: borderRadius.sm,
    backgroundColor: '#e8f5e9',
    color: colors.secondaryDark,
  },
  failurePanel: {
    backgroundColor: colors.white,
    border: `1px solid ${colors.border}`,
    borderRadius: borderRadius.md,
    padding: spacing.md,
  },
  failureHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: spacing.md,
    marginBottom: spacing.sm,
  },
  dangerButton: {
    padding: `${spacing.xs} ${spacing.md}`,
    borderRadius: borderRadius.sm,
    border: `1px solid ${colors.error}`,
    backgroundColor: colors.white,
    color: colors.error,
    cursor: 'pointer',
    whiteSpace: 'nowrap' as const,
  },
  linkDangerButton: {
    padding: 0,
    border: 'none',
    background: 'none',
    color: colors.error,
    cursor: 'pointer',
    fontSize: typography.small.fontSize,
    textDecoration: 'underline',
  },
  errorText: {
    color: colors.error,
  },
};
